import { useFormik } from "formik";
import { useEffect, useState } from "react";
import * as yup from "yup";
import { useNavigate, useParams } from "react-router-dom";
import BaseButton from "Components/Base/BaseButton";
import BaseInput from "Components/Base/BaseInput";
import Loader from "Components/Base/BaseLoader";
import { toast } from "react-toastify";
import { checkStatusCodeSuccess } from "Components/constants/Common";
import {
  emailRegex,
  GSTINRegex,
  InputPlaceHolder,
  numberRegex,
  SelectPlaceHolder,
  validationMessages,
  zipcodeRegex,
} from "Components/constants/validation";
import { BaseSelect } from "Components/Base/BaseSelect";
import { listOfBank, listOfCity, listOfCountry, listOfState } from "Api/common";
import {
  AccountHolderNameLabel,
  AccountNumberKey,
  AccountNumberLabel,
  AlternatePhoneNumberLabel,
  BankNameLabel,
  BillingAddressLabel,
  CityLabel,
  CountryLabel,
  EmailLabel,
  IFSCLabel,
  PhoneNumberLabel,
  PostalCodeLabel,
  ReAccountNumberLabel,
  SameAsBillingAddressLabel,
  SameAsBillingAddressTooltipLabel,
  ShippingAddressLabel,
  StateLabel,
  StreetLabel,
} from "Components/constants/Staff";
import {
  cancelLabel,
  submitLabel,
  updateLabel,
} from "Components/constants/Role";
import {
  editVendorLabel,
  gstinLabel,
  newVendorLabel,
  organization,
  organizationLabel,
  organizationPlaceHolder,
  panLabel,
  vendorLabel,
  vendorMsmeOptions,
  vendorName,
} from "Components/constants/Vendor";
import { createVendor, updateVendor, viewVendor } from "Api/Vendor";
import BaseRadioGroup from "Components/Base/BaseRadio";

const validationSchema = yup.object({
  vendorName: yup.string().required(validationMessages.required(vendorName)),
  phoneNumber: yup
    .string()
    .required(validationMessages.required(PhoneNumberLabel))
    .matches(
      numberRegex,
      validationMessages.contactLength(PhoneNumberLabel, 10)
    ),
  email: yup
    .string()
    .required(validationMessages.required(EmailLabel))
    .matches(emailRegex, validationMessages.format(EmailLabel)),
  alternatePhone: yup
    .string()
    .matches(
      numberRegex,
      validationMessages.contactLength(AlternatePhoneNumberLabel, 10)
    ),
  panNo: yup.string().required(validationMessages.required(panLabel)),
  accountHolderName: yup.string(),
  bankName: yup.string(),
  accountNumber: yup
    .string()
    .min(10, validationMessages.minLength(AccountNumberLabel, 10))
    .max(16, validationMessages.maxLength(AccountNumberLabel, 16)),
  reenterAccountNumber: yup
    .string()
    .oneOf(
      [yup.ref(AccountNumberKey)],
      validationMessages.passwordsMatch(
        AccountNumberLabel,
        ReAccountNumberLabel
      )
    )
    .min(10, validationMessages.minLength(ReAccountNumberLabel, 10))
    .max(16, validationMessages.maxLength(ReAccountNumberLabel, 16)),
  ifsc: yup
    .string()
    .min(11, validationMessages.maxChar(IFSCLabel, 11))
    .max(11, validationMessages.maxChar(IFSCLabel, 11)),
  organization: yup
    .string()
    .required(validationMessages.required(organization)),
  gstin_no: yup.string().max(15, validationMessages.maxLength(gstinLabel, 15)),
  shippingPostalCode: yup
    .string()
    .matches(
      zipcodeRegex,
      validationMessages.contactLength(PostalCodeLabel, 6)
    ),
  billingPostalCode: yup
    .string()
    .matches(
      zipcodeRegex,
      validationMessages.contactLength(PostalCodeLabel, 6)
    ),
});

const AddVendor = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  document.title = id ? editVendorLabel : newVendorLabel;
  const [loader, setLoader] = useState(false);
  const [viewVendorData, setViewVendorData] = useState<any>([]);
  const [btnLoader, setBtnLoader] = useState(false);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [city, setCity] = useState([]);
  const [bank, setBank] = useState([]);
  const [sameAsBilling, setSameAsBilling] = useState(false);
  const [msmeRegistered, setMsmeRegistered] = useState<string>("false");

  const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setMsmeRegistered(event.target.value);
  };

  const fetchCountry = () => {
    // setLoader(true);
    let data;
    const payload = {};
    listOfCountry(payload)
      .then((res) => {
        if (checkStatusCodeSuccess(res?.statusCode)) {
          data = res?.data;
          setCountries(
            data?.map((item: any) => ({
              value: item?.id,
              label: item?.country_name,
              id: item?.id,
            }))
          );
        } else {
          toast.error(res?.message);
        }
      })
      .catch((err) => {
        return err;
      })
      .finally(() => {
        // setLoader(false);
      });
  };

  const fetchState = (id: number) => {
    // setLoader(true);
    let data;
    const payload = {
      condition: {
        country_id: id,
      },
    };
    listOfState(payload)
      .then((res) => {
        if (checkStatusCodeSuccess(res?.statusCode)) {
          data = res?.data;
          setStates(
            data?.map((item: any) => ({
              value: item?.id,
              label: item?.state_name,
              id: item?.id,
            }))
          );
        } else {
          toast.error(res?.message);
        }
      })
      .catch((err: any) => {
        setStates([]);
        fetchCity(null);
        return err;
      })
      .finally(() => {
        // setLoader(false);
      });
  };

  const fetchCity = (id: any) => {
    // setLoader(true);
    let data;
    const payload = {
      condition: {
        state_id: id,
      },
    };
    listOfCity(payload)
      .then((res) => {
        if (checkStatusCodeSuccess(res?.statusCode)) {
          data = res?.data;
          setCity(
            data?.map((item: any) => ({
              value: item?.id,
              label: item?.city_name,
              id: item?.id,
            }))
          );
        } else {
          toast.error(res?.message);
        }
      })
      .catch((err) => {
        setCity([]);
        return err;
      })
      .finally(() => {
        // setLoader(false);
      });
  };

  const fetchBank = () => {
    // setLoader(true);
    let data;
    listOfBank({})
      .then((res) => {
        if (checkStatusCodeSuccess(res?.statusCode)) {
          data = res?.data;
          setBank(
            data?.map((item: any) => ({
              id: item?.id,
              label: item?.bank_name,
              value: item?.id,
            }))
          );
        }
      })
      .catch((err) => {
        setBank([]);
        return err;
      })
      .finally(() => {
        // setLoader(false);
      });
  };

  const handleSameAsBilling = () => {
    setSameAsBilling(!sameAsBilling);
    if (!sameAsBilling) {
      formik.setValues((prevValues) => ({
        ...prevValues,
        shippingStreet: prevValues.billingStreet,
        shippingCountry: prevValues.billingCountry,
        shippingState: prevValues.billingState,
        shippingCity: prevValues.billingCity,
        shippingPostalCode: prevValues.billingPostalCode,
      }));
    } else {
      formik.setValues((prevValues) => ({
        ...prevValues,
        shippingStreet: "",
        shippingCountry: "",
        shippingState: "",
        shippingCity: "",
        shippingPostalCode: "",
      }));
    }
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      vendorName: id ? viewVendorData?.name : "",
      phoneNumber: id ? viewVendorData?.contact_no || "" : "",
      email: id ? viewVendorData?.email || "" : "",
      alternatePhone: id ? viewVendorData?.alternative_contact_no || "" : "",
      panNo: id ? viewVendorData?.pan_no : "",
      msme: id ? viewVendorData?.msme_registered || false : false,
      accountHolderName: id
        ? viewVendorData?.bankDetail?.account_holder_name || ""
        : "",
      bankName: id ? viewVendorData?.bankDetail?.bank_id || "" : "",
      accountNumber: id ? viewVendorData?.bankDetail?.account_number || "" : "",
      reenterAccountNumber: id
        ? viewVendorData?.bankDetail?.account_number || ""
        : "",
      ifsc: id ? viewVendorData?.bankDetail?.ifsc_code || "" : "",
      organization: id ? viewVendorData?.bankDetail?.organization || "" : "",
      gstin_no: id ? viewVendorData?.bankDetail?.gstin_no || "" : "",
      billingStreet: id ? viewVendorData?.address?.billing_address_1 || "" : "",
      billingCountry: id ? viewVendorData?.address?.billing_country_id : "",
      billingState: id ? viewVendorData?.address?.billing_state_id : "",
      billingCity: id ? viewVendorData?.address?.billing_city_id : "",
      billingPostalCode: id
        ? viewVendorData?.address?.billing_postal_code || ""
        : "",
      shippingStreet: id
        ? viewVendorData?.address?.shipping_address_1 || ""
        : "",
      shippingCountry: id ? viewVendorData?.address?.shipping_country_id : "",
      shippingState: id ? viewVendorData?.address?.shipping_state_id : "",
      shippingCity: id ? viewVendorData?.address?.shipping_city_id : "",
      shippingPostalCode: id
        ? viewVendorData?.address?.shipping_postal_code || ""
        : "",
    },
    validationSchema,
    onSubmit: (values) => {
      setBtnLoader(true);
      const payload = {
        name: values.vendorName,
        contact_no: String(values.phoneNumber),
        email: values.email,
        alternative_contact_no: String(values.alternatePhone),
        pan_no: values.panNo,
        msme_registered: values.msme,
        type_of_user: vendorLabel,
        staffBankDetail: {
          bank_id: values.bankName || null,
          account_holder_name: values.accountHolderName || null,
          account_number: values.accountNumber || null,
          ifsc_code: values.ifsc || null,
          organization: values.organization || null,
          gstin_no: values.gstin_no || null,
        },
        staffAddress: {
          billing_address_1: values.billingStreet,
          billing_country_id: values.billingCountry || null,
          billing_state_id: values.billingState || null,
          billing_city_id: values.billingCity || null,
          billing_postal_code: values.billingPostalCode || null,
          shipping_same_as_billing: sameAsBilling,
          shipping_address_1: values.shippingStreet,
          shipping_country_id: values.shippingCountry || null,
          shipping_state_id: values.shippingState || null,
          shipping_city_id: values.shippingCity || null,
          shipping_postal_code: values.shippingPostalCode || null,
        },
      };
      if (id) {
        updateVendor(Number(id), payload)
          .then((res) => {
            if (checkStatusCodeSuccess(res?.statusCode)) {
              toast.success(res?.message);
              navigate("/vendor");
            } else {
              toast.error(res?.message);
            }
          })
          .catch((err) => {
            toast.error(err?.response?.data?.message || err?.message);
            return err;
          })
          .finally(() => {
            setBtnLoader(false);
          });
      } else {
        createVendor(payload)
          .then((res) => {
            if (checkStatusCodeSuccess(res?.statusCode)) {
              toast.success(res?.message);
              navigate("/vendor");
            } else {
              toast.error(res?.message);
            }
          })
          .catch((err) => {
            toast.error(err?.response?.data?.message || err?.message);
            return err;
          })
          .finally(() => {
            setBtnLoader(false);
          });
      }
    },
  });

  useEffect(() => {
    if (formik.isSubmitting && Object.keys(formik.errors).length > 0) {
      const firstErrorKey = Object.keys(formik.errors)[0];

      if (firstErrorKey) {
        const errorElement: HTMLElement | null = document.querySelector(
          `[name="${firstErrorKey}"], #${firstErrorKey}`
        );

        if (errorElement) {
          errorElement.scrollIntoView({ behavior: "smooth", block: "center" });
          if (
            errorElement.tagName === "INPUT" ||
            errorElement.tagName === "SELECT"
          ) {
            errorElement.focus();
          } else {
            const selectControl = errorElement.closest(".select__control");
            if (selectControl) {
              selectControl.scrollIntoView({
                behavior: "smooth",
                block: "center",
              });
              const selectInput = selectControl.querySelector("input, select");
              if (selectInput) {
                (selectInput as HTMLElement).focus();
              }
            }
          }
        }
      }
    }
  }, [formik.isSubmitting, formik.errors]);

  const fetchViewRole = () => {
    if (id) {
      setLoader(true);
      viewVendor(Number(id))
        .then((res) => {
          if (checkStatusCodeSuccess(res.statusCode)) {
            const roleData = res.data;
            setViewVendorData(roleData);
          }
        })
        .catch((error) => {
          return error;
        })
        .finally(() => {
          setLoader(false);
        });
    }
  };

  useEffect(() => {
    setSameAsBilling(viewVendorData?.address?.shipping_same_as_billing);
    if (viewVendorData?.address?.billing_country_id) {
      fetchState(Number(viewVendorData?.address?.billing_country_id));
    }
    if (viewVendorData?.address?.billing_state_id) {
      fetchCity(Number(viewVendorData?.address?.billing_state_id));
    }
    if (viewVendorData?.address?.shipping_country_id) {
      fetchState(Number(viewVendorData?.address?.shipping_country_id));
    }
    if (viewVendorData?.address?.shipping_state_id) {
      fetchCity(Number(viewVendorData?.address?.shipping_state_id));
    }
  }, [viewVendorData]);

  useEffect(() => {
    if (id) {
      fetchViewRole();
    }
    fetchCountry();
    fetchBank();
  }, []);

  return (
    <div className="page-content">
      {loader && <Loader />}
      <div className="flex-grow-1 mx-3">
        <div className="mb-1 mx-2 d-flex justify-content-between align-items-center">
          <span className="fs-4">{id ? editVendorLabel : newVendorLabel}</span>
          <button
            className="btn btn-danger"
            onClick={() => navigate("/vendor")}
          >
            <i className="mdi mdi-arrow-left" />
          </button>
        </div>
        <div className="card word-wrap">
          <div className="card-body">
            <form onSubmit={formik.handleSubmit} className="mt-2 mb-4">
              <div className="row">
                <div className="col-md-3 col-sm-6 col-12">
                  <BaseInput
                    name="vendorName"
                    label={vendorName}
                    placeholder={InputPlaceHolder(vendorName)}
                    value={formik.values.vendorName}
                    onChange={formik.handleChange}
                    handleBlur={formik.handleBlur}
                    error={formik.errors.vendorName}
                    touched={formik.touched.vendorName}
                    required
                  />
                </div>
                <div className="col-md-3 col-sm-6 col-12">
                  <BaseInput
                    name="phoneNumber"
                    type="number"
                    label={PhoneNumberLabel}
                    placeholder={InputPlaceHolder(PhoneNumberLabel)}
                    value={formik.values.phoneNumber}
                    onChange={formik.handleChange}
                    handleBlur={formik.handleBlur}
                    error={formik.errors.phoneNumber}
                    maxLength={10}
                    touched={formik.touched.phoneNumber}
                    required
                  />
                </div>
                <div className="col-md-3 col-sm-6 col-12">
                  <BaseInput
                    name="email"
                    label={EmailLabel}
                    placeholder={InputPlaceHolder(EmailLabel)}
                    type="email"
                    value={formik.values.email}
                    onChange={formik.handleChange}
                    handleBlur={formik.handleBlur}
                    error={formik.errors.email}
                    touched={formik.touched.email}
                    required
                  />
                </div>
                <div className="col-md-3 col-sm-6 col-12">
                  <BaseInput
                    name="alternatePhone"
                    type="number"
                    maxLength={10}
                    label={AlternatePhoneNumberLabel}
                    placeholder={InputPlaceHolder(AlternatePhoneNumberLabel)}
                    value={formik.values.alternatePhone}
                    onChange={formik.handleChange}
                    handleBlur={formik.handleBlur}
                    error={formik.errors.alternatePhone}
                    touched={formik.touched.alternatePhone}
                  />
                </div>
              </div>
              <div className="row mt-3">
                <div className="col-md-3 col-sm-6 col-12">
                  <BaseInput
                    name="panNo"
                    label={panLabel}
                    placeholder={InputPlaceHolder(panLabel)}
                    value={formik.values.panNo}
                    onChange={formik.handleChange}
                    handleBlur={formik.handleBlur}
                    error={formik.errors.panNo}
                    touched={formik.touched.panNo}
                    required
                  />
                </div>
                <div className="col-md-3 col-sm-6 col-12">
                  <label className="form-label d-flex mb-3">
                    MSME Registered?
                  </label>
                  <BaseRadioGroup
                    name="msmeRegistered"
                    options={vendorMsmeOptions}
                    onChange={handleRadioChange}
                    selectedValue={msmeRegistered}
                    className="form-check-success d-flex gap-2"
                    classNameLabel="mx-2"
                  />
                </div>
                <div className="col-md-3 col-sm-6 col-12">
                  <BaseInput
                    name="accountHolderName"
                    label={AccountHolderNameLabel}
                    placeholder={InputPlaceHolder(AccountHolderNameLabel)}
                    type="text"
                    value={formik.values.accountHolderName}
                    onChange={formik.handleChange}
                    handleBlur={formik.handleBlur}
                    error={formik.errors.accountHolderName}
                    touched={formik.touched.accountHolderName}
                  />
                </div>
                <div className="col-md-3 col-sm-6 col-12">
                  <BaseSelect
                    name="bankName"
                    label={BankNameLabel}
                    options={bank}
                    value={formik.values.bankName}
                    placeholder={SelectPlaceHolder(BankNameLabel)}
                    handleChange={(field: any, value: any) => {
                      formik.setFieldValue(field, value);
                    }}
                    handleBlur={formik.handleBlur}
                    error={formik.errors.bankName}
                    touched={formik.touched.bankName}
                  />
                </div>
              </div>
              <div className="row mt-3">
                <div className="col-md-3 col-sm-6 col-12">
                  <BaseInput
                    name="accountNumber"
                    label={AccountNumberLabel}
                    type="number"
                    placeholder={InputPlaceHolder(AccountNumberLabel)}
                    value={formik.values.accountNumber}
                    onChange={formik.handleChange}
                    handleBlur={formik.handleBlur}
                    error={formik.errors.accountNumber}
                    touched={formik.touched.accountNumber}
                  />
                </div>
                <div className="col-md-3 col-sm-6 col-12">
                  <BaseInput
                    name="reenterAccountNumber"
                    type="number"
                    label={ReAccountNumberLabel}
                    placeholder={InputPlaceHolder(ReAccountNumberLabel)}
                    value={formik.values.reenterAccountNumber}
                    onChange={formik.handleChange}
                    handleBlur={formik.handleBlur}
                    error={formik.errors.reenterAccountNumber}
                    touched={formik.touched.reenterAccountNumber}
                  />
                </div>
                <div className="col-md-3 col-sm-6 col-12">
                  <BaseInput
                    name="ifsc"
                    label={IFSCLabel}
                    placeholder={InputPlaceHolder(IFSCLabel)}
                    type="text"
                    value={formik.values.ifsc}
                    onChange={formik.handleChange}
                    handleBlur={formik.handleBlur}
                    error={formik.errors.ifsc}
                    touched={formik.touched.ifsc}
                  />
                </div>
              </div>
              <div className="row mt-4">
                <div className="col-12 fs-5">{BillingAddressLabel}</div>
                <div className="col-md-3 col-sm-6 col-12 mt-3">
                  <BaseInput
                    name="organization"
                    label={organizationLabel}
                    placeholder={InputPlaceHolder(organizationPlaceHolder)}
                    type="text"
                    value={formik.values.organization}
                    onChange={formik.handleChange}
                    handleBlur={formik.handleBlur}
                    error={formik.errors.organization}
                    touched={formik.touched.organization}
                    required
                  />
                </div>
                <div className="col-3 mt-3">
                  <BaseInput
                    name="gstin_no"
                    label={gstinLabel}
                    placeholder={InputPlaceHolder(gstinLabel)}
                    type="text"
                    value={formik.values.gstin_no}
                    onChange={formik.handleChange}
                    handleBlur={formik.handleBlur}
                    error={formik.errors.gstin_no}
                    touched={formik.touched.gstin_no}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col-md-3 col-sm-6 col-12 mt-3">
                  <BaseInput
                    name="billingStreet"
                    type="textarea"
                    label={StreetLabel}
                    placeholder={InputPlaceHolder(StreetLabel)}
                    value={formik.values.billingStreet}
                    onChange={formik.handleChange}
                    handleBlur={formik.handleBlur}
                    error={formik.errors.billingStreet}
                    touched={formik.touched.billingStreet}
                  />
                </div>
                <div className="col-md-3 col-sm-6 col-12 mt-3">
                  <BaseSelect
                    name="billingCountry"
                    label={CountryLabel}
                    options={countries}
                    value={formik.values.billingCountry}
                    placeholder={SelectPlaceHolder(CountryLabel)}
                    handleChange={(field: any, value: any) => {
                      formik.setFieldValue(field, value);
                      fetchState(value);
                    }}
                    handleBlur={formik.handleBlur}
                    error={formik.errors.billingCountry}
                    touched={formik.touched.billingCountry}
                  />
                </div>
                <div className="col-md-3 col-sm-6 col-12 mt-3">
                  <BaseSelect
                    name="billingState"
                    label={StateLabel}
                    options={states}
                    value={formik.values.billingState}
                    placeholder={SelectPlaceHolder(StateLabel)}
                    handleChange={(field: any, value: any) => {
                      formik.setFieldValue(field, value);
                      fetchCity(value);
                    }}
                    handleBlur={formik.handleBlur}
                    error={formik.errors.billingState}
                    touched={formik.touched.billingState}
                  />
                </div>
                <div className="col-md-3 col-sm-6 col-12 mt-3">
                  <BaseSelect
                    name="billingCity"
                    label={CityLabel}
                    options={city}
                    value={formik.values.billingCity}
                    placeholder={SelectPlaceHolder(CityLabel)}
                    handleChange={(field: any, value: any) => {
                      formik.setFieldValue(field, value);
                    }}
                    handleBlur={formik.handleBlur}
                    error={formik.errors.billingCity}
                    touched={formik.touched.billingCity}
                  />
                </div>
                <div className="col-md-3 col-sm-6 col-12 mt-3">
                  <BaseInput
                    name="billingPostalCode"
                    type="number"
                    label={PostalCodeLabel}
                    maxLength={6}
                    placeholder={InputPlaceHolder(PostalCodeLabel)}
                    value={formik.values.billingPostalCode}
                    onChange={formik.handleChange}
                    handleBlur={formik.handleBlur}
                    error={formik.errors.billingPostalCode}
                    touched={formik.touched.billingPostalCode}
                  />
                </div>
              </div>
              <div className="row mt-4">
                <div className="col-12 fs-5 d-flex justify-content-start align-items-center">
                  {ShippingAddressLabel}
                  <div className="d-flex flex-column align-items-center justify-content-start">
                    <div
                      className="form-check form-check-success form-switch form-switch-md ms-3"
                      dir="ltr"
                    >
                      <input
                        type="checkbox"
                        className="form-check-input shadow-none"
                        id="customSwitchsizemd"
                        checked={sameAsBilling}
                        onChange={handleSameAsBilling}
                      />
                      <label
                        className="form-check-label custom-label fs-5"
                        htmlFor="customSwitchsizemd"
                      >
                        {SameAsBillingAddressLabel}
                      </label>
                      <i className="mdi mdi-help-circle-outline ms-1 mt-1 text-info fs-5 cursor-pointer align-middle tooltip-container-data">
                        <span className="tooltip-text font-weight-normal">
                          {SameAsBillingAddressTooltipLabel}
                        </span>
                      </i>
                    </div>
                  </div>
                </div>
                <div className="col-md-3 col-sm-6 col-12 mt-3">
                  <BaseInput
                    name="shippingStreet"
                    type="textarea"
                    label={StreetLabel}
                    placeholder={InputPlaceHolder(StreetLabel)}
                    value={formik.values.shippingStreet}
                    onChange={formik.handleChange}
                    handleBlur={formik.handleBlur}
                    error={formik.errors.shippingStreet}
                    touched={formik.touched.shippingStreet}
                    disabled={sameAsBilling}
                  />
                </div>
                <div className="col-md-3 col-sm-6 col-12 mt-3">
                  <BaseSelect
                    name="shippingCountry"
                    label={CountryLabel}
                    options={countries}
                    value={formik.values.shippingCountry}
                    placeholder={SelectPlaceHolder(CountryLabel)}
                    handleChange={(field: any, value: any) => {
                      formik.setFieldValue(field, value);
                      fetchState(value);
                    }}
                    handleBlur={formik.handleBlur}
                    error={formik.errors.shippingCountry}
                    touched={formik.touched.shippingCountry}
                    isDisabled={sameAsBilling}
                  />
                </div>
                <div className="col-md-3 col-sm-6 col-12 mt-3">
                  <BaseSelect
                    name="shippingState"
                    label={StateLabel}
                    options={states}
                    value={formik.values.shippingState}
                    placeholder={SelectPlaceHolder(StateLabel)}
                    handleChange={(field: any, value: any) => {
                      formik.setFieldValue(field, value);
                      fetchCity(value);
                    }}
                    handleBlur={formik.handleBlur}
                    error={formik.errors.shippingState}
                    touched={formik.touched.shippingState}
                    isDisabled={sameAsBilling}
                  />
                </div>
                <div className="col-md-3 col-sm-6 col-12 mt-3">
                  <BaseSelect
                    name="shippingCity"
                    label={CityLabel}
                    options={city}
                    value={formik.values.shippingCity}
                    placeholder={SelectPlaceHolder(CityLabel)}
                    handleChange={(field: any, value: any) => {
                      formik.setFieldValue(field, value);
                    }}
                    handleBlur={formik.handleBlur}
                    error={formik.errors.shippingCity}
                    touched={formik.touched.shippingCity}
                    isDisabled={sameAsBilling}
                  />
                </div>
                <div className="col-md-3 col-sm-6 col-12 mt-3">
                  <BaseInput
                    name="shippingPostalCode"
                    type="number"
                    maxLength={6}
                    label={PostalCodeLabel}
                    placeholder={InputPlaceHolder(PostalCodeLabel)}
                    value={formik.values.shippingPostalCode}
                    onChange={formik.handleChange}
                    handleBlur={formik.handleBlur}
                    error={formik.errors.shippingPostalCode}
                    touched={formik.touched.shippingPostalCode}
                    disabled={sameAsBilling}
                  />
                </div>
              </div>
              <div className="row mt-3 d-flex justify-content-end">
                <div className="col-auto d-flex justify-content-end gap-2 text-end">
                  <BaseButton
                    type="submit"
                    label={id ? updateLabel : submitLabel}
                    color="success"
                    loader={btnLoader}
                    disable={btnLoader}
                  />
                  <BaseButton
                    type="button"
                    label={cancelLabel}
                    onClick={() => navigate("/vendor")}
                    color="danger"
                  />
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddVendor;
